import { Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { useProposalConfigurations } from '@/hooks/commercial/use-proposal-configurations';
import { StepConfigurationModal } from './step-configuration-modal';
import { StepConfigurationsEmpty } from './step-configurations-empty';
import { StepConfigurationsList } from './step-configurations-list';

interface StepConfigurationsContainerProps {
  proposalId?: string;
  onValidationChange?: (isValid: boolean) => void;
}

const CREATE_PROFILE_PATH = '/production/configuration-profile/form/create';

export function StepConfigurationsContainer({
  proposalId,
  onValidationChange,
}: StepConfigurationsContainerProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const [activeGroupId, setActiveGroupId] = useState<string | null>(null);

  const { data, isError, isLoading, refetch } = useProposalConfigurations(proposalId);

  const hasTrackers = data?.hasTrackers ?? false;
  const groups = data?.groups ?? [];
  const isValidated = data?.isValidated ?? true;
  const activeGroup = groups.find((group) => group.id === activeGroupId);

  // No trackers = automatically validated
  useEffect(() => {
    if (!isLoading) {
      onValidationChange?.(!hasTrackers || isValidated);
    }
  }, [isLoading, hasTrackers, isValidated, onValidationChange]);

  const handleConfigure = (groupId: string) => {
    setActiveGroupId(groupId);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setActiveGroupId(null);
  };

  return (
    <div className="space-y-6">
      {hasTrackers && (
        <div className="flex items-center justify-end">
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5"
            onClick={() => window.open(CREATE_PROFILE_PATH, '_blank')}
          >
            <Plus className="h-3.5 w-3.5" />
            Criar Perfil
          </Button>
        </div>
      )}

      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <p className="text-sm text-muted-foreground">Carregando configurações...</p>
        </div>
      )}

      {!isLoading && isError && (
        <div className="flex flex-col items-center justify-center gap-3 py-12">
          <p className="text-sm text-muted-foreground">Erro ao carregar configurações.</p>
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            Tentar novamente
          </Button>
        </div>
      )}

      {!isLoading && !isError && !hasTrackers && <StepConfigurationsEmpty />}

      {!isLoading && !isError && hasTrackers && (
        <StepConfigurationsList groups={groups} onConfigure={handleConfigure} />
      )}

      <StepConfigurationModal
        open={modalOpen}
        onClose={handleCloseModal}
        proposalId={proposalId}
        groupId={activeGroupId ?? undefined}
        selectedProfiles={activeGroup?.selectedProfiles ?? []}
        totalDevices={activeGroup?.totalDevices ?? 0}
      />
    </div>
  );
}
